import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, ExternalLink, MessageSquare, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { trackSupportPageViewed } from "@/lib/analytics";

const Support = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackSupportPageViewed();
  }, []);

  const faqs = [
    {
      question: "How do credits work?",
      answer: "Credits are used to generate AI-powered interior designs. Each design generation consumes one credit. Free users receive 3 one-time trial credits, while paid plans offer monthly credits based on the subscription tier (Pro: 30/month, Business: 120/month)."
    },
    {
      question: "How do I upgrade my plan?",
      answer: "You can upgrade your plan by going to Settings > Subscription or visiting the Pricing page. Select the plan that best fits your needs and complete the payment process."
    },
    {
      question: "How do I cancel my subscription?",
      answer: "To cancel your subscription, go to Settings > Subscription and click on 'Manage Subscription'. From there, you can cancel your current plan. Your access will continue until the end of your billing period."
    },
    {
      question: "What happens to my projects if I downgrade?",
      answer: "Your existing projects will remain accessible. However, you may not be able to create new designs beyond the limits of your new plan. We recommend saving any important designs before downgrading."
    },
    {
      question: "How do I delete my account?",
      answer: "You can request account deletion from Settings > Account. Please note that this action is irreversible and all your data, including saved designs, will be permanently deleted."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button onClick={() => navigate(-1)} variant="outline" size="sm">
            <ArrowLeft className="mr-1 h-4 w-4" />
            <span className="sm:hidden">Back</span>
            <span className="hidden sm:inline">Go Back</span>
          </Button>
          <h1 className="text-xl sm:text-2xl md:text-4xl font-bold">Support</h1>
        </div>

        <div className="space-y-6">
          {/* Contact Us */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Have a question or need assistance? Our support team is here to help.
              </p>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="font-medium">Email Support</p>
                <a 
                  href="mailto:hello.dc@atheniaai.com" 
                  className="text-primary hover:underline"
                >
                  hello.dc@atheniaai.com
                </a>
                <p className="text-sm text-muted-foreground mt-2">
                  Expected response time: 24-48 hours
                </p>
              </div>
            </CardContent>
          </Card>

          {/* FAQ */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                Frequently Asked Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* System Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="h-5 w-5 text-primary" />
                System Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Check our system status page for real-time information about service availability.
              </p>
              <Button 
                variant="outline" 
                onClick={() => navigate("/status")}
              >
                View System Status
              </Button>
            </CardContent>
          </Card>

          {/* Feedback */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                Feedback & Feature Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                We love hearing from our users! Share your ideas, suggestions, or feedback to help us improve Decor Canvas.
              </p>
              <a 
                href="mailto:hello.dc@atheniaai.com?subject=Feedback%20for%20Decor%20Canvas" 
                className="text-primary hover:underline"
              >
                Send us your feedback →
              </a>
            </CardContent>
          </Card>

          {/* Company Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Company Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="font-medium">Athenia AI Labs Private Limited</p>
              <p className="text-muted-foreground">
                Product: Decor Canvas - Interior Decor AI Designer
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                We're committed to revolutionizing interior design through the power of artificial intelligence, 
                making professional-quality design accessible to everyone.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Support;
